/* ---------------------------------------------------------------------------
 * LIVE MOCKUP — assets/live-mockup.js
 *
 * Composites a 3D floating-frame product mockup from:
 *   • the artwork image (primary or alt metafield — whichever aspect ratio
 *     is closest to the customer-selected size)
 *   • the currently selected frame texture (oak / black / gold / natural /
 *     walnut / white)
 *
 * Floating-frame profile matches the AR Wall View:
 *     0.50"  frame profile width
 *     0.25"  reveal gap (black) between frame and artwork
 *     1.50"  depth
 * Textures are rendered at a consistent real-world scale (tiled if needed)
 * and rotated 90° for the left/right frame pieces so the grain runs along
 * the frame direction like a real mitred frame.
 *
 * Scene rotation: +15° around Y. This exposes the LEFT depth strip (frame
 * side for framed, mirrored artwork for stretched canvas).
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  if (customElements.get('live-mockup')) return;

  // Cap DPR at 3 so iPhones (DPR 3) render the front face at native pixel
  // density — capping at 2 previously made the preview read as soft on
  // retina phones. Desktop browsers rarely exceed DPR 2, so this change is
  // effectively mobile-only and the raster cost is one-time per variant
  // change (drag rotation is pure CSS transform, no re-raster).
  var DPR = Math.min(window.devicePixelRatio || 1, 3);
  // Side canvases are foreshortened to narrow strips in the final
  // render — paying for full DPR on them is wasted pixels. Capping at
  // 1.5 halves their pixel buffer on retina with no visible loss.
  var SIDE_DPR = Math.min(window.devicePixelRatio || 1, 1.5);
  // Modern browsers handle 3D intersections correctly without sub-pixel gaps.
  // Setting this to 0 prevents the front edge from unnaturally jutting out 1px.
  var FACE_OVERLAP = 0;
  // Transparent canvas pixels let texture filtering smooth the rotated edges.
  var EDGE_PAD = 1;

  function loadImage(src) {
    return new Promise(function (resolve) {
      if (!src) { resolve(null); return; }
      var img = new Image();
      img.onerror = function () { resolve(null); };
      img.onload = function () {
        // Decode the full-resolution bitmap before the first canvas draw.
        // iOS Safari otherwise composites a low-res decode of large (2400px)
        // images until they're "needed" again, so the initial drawImage reads
        // soft until a later re-render. decode() forces the full decode up
        // front; on failure / unsupported we fall back to the loaded image.
        if (typeof img.decode === 'function') {
          img.decode().then(function () { resolve(img); }, function () { resolve(img); });
        } else {
          resolve(img);
        }
      };
      img.src = src;
    });
  }

  function parseSize(str) {
    if (!str) return null;
    var m = String(str).replace(/[""″]/g, '')
      .match(/(\d+(?:\.\d+)?)\s*[x×X*]\s*(\d+(?:\.\d+)?)/);
    if (!m) return null;
    return { a: parseFloat(m[1]), b: parseFloat(m[2]) };
  }

  function parseDepth(str) {
    if (!str) return null;
    var m = String(str).replace(/[""″]/g, '').match(/(\d+(?:\.\d+)?)/);
    return m ? parseFloat(m[1]) : null;
  }

  function frameKeyFromValue(v) {
    if (!v) return null;
    var s = String(v).toLowerCase();
    if (s.indexOf('stretch') >= 0) return null;
    if (s.indexOf('no frame') >= 0) return null;
    if (s.indexOf('unframed') >= 0) return null;
    if (s.indexOf('black') >= 0) return 'black';
    if (s.indexOf('walnut') >= 0) return 'walnut';
    if (s.indexOf('gold') >= 0) return 'gold';
    if (s.indexOf('white') >= 0) return 'white';
    if (s.indexOf('oak') >= 0) return 'oak';   // also "natural oak"
    if (s.indexOf('natural') >= 0) return 'natural';
    return null;
  }

  /**
   * Pre-render a frame strip onto an offscreen canvas.
   *
   * NO TILING — the texture is stretched to cover the strip in one pass
   * so there are no repeat seams (which show up as "bamboo joints" on
   * textured frames and as faint bands on solid-white frames).
   *
   * For vertical pieces (left/right) the texture is rotated 90° so the
   * grain runs along the frame length, matching a real mitred frame.
   *
   * A symmetric cross-profile gradient is applied on top to fake the
   * molding. Intensity is deliberately low so the frame no longer
   * reads as heavily shadowed — just a subtle bevel.
   *
   * Strips are cached by frame texture identity + dimensions +
   * orientation. Switching between frames reuses the cached strip
   * instead of re-rasterising, which eliminates the per-click lag.
   */
  // Frames whose wood tone is pale — the cross-profile bevel gradient
  // gets softened on these so the shadows don't crush the texture.
  var _LIGHT_FRAMES = { natural: 1, oak: 1, white: 1, gold: 1 };

  var _STRIP_CACHE = new Map();
  function makeFrameStrip(tex, stripLength, stripThickness, vertical, frameKey) {
    var L = Math.max(1, Math.round(stripLength));
    var T = Math.max(1, Math.round(stripThickness));
    var isLight = !!(frameKey && _LIGHT_FRAMES[frameKey]);
    // tex is an HTMLImageElement — .src is a stable identity key. frameKey
    // affects the gradient, so it's part of the cache key.
    var key = (tex && tex.src ? tex.src : '?') + '|' + L + 'x' + T + '|' +
      (vertical ? 'v' : 'h') + '|' + (isLight ? 'L' : 'D');
    var cached = _STRIP_CACHE.get(key);
    if (cached) return cached;

    var off = document.createElement('canvas');
    var w = vertical ? T : L;
    var h = vertical ? L : T;
    off.width = w;
    off.height = h;
    var ctx = off.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // 1) Stretch the texture over the whole strip — single pass, no seams.
    if (vertical) {
      ctx.save();
      ctx.translate(T, 0);
      ctx.rotate(Math.PI / 2);
      // Post-rotation drawing space: x runs along length, y runs across thickness
      ctx.drawImage(tex, 0, 0, L, T);
      ctx.restore();
    } else {
      ctx.drawImage(tex, 0, 0, L, T);
    }

    // 2) Molding cross-profile — symmetric so it's correct on all four
    //    sides. Light frames (like white and natural) get gentler lighting than dark
    // woods (oak / walnut / black) where heavy shadows flatten the
    // grain and make the frame look muddy.
    var edge = isLight ? 0.07 : 0.20;
    var mid = isLight ? 0.015 : 0.04;
    var hi = isLight ? 0.06 : 0.10;
    var grad = vertical
      ? ctx.createLinearGradient(0, 0, w, 0)
      : ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0.00, 'rgba(0,0,0,' + edge + ')');
    grad.addColorStop(0.14, 'rgba(0,0,0,' + mid + ')');
    grad.addColorStop(0.44, 'rgba(255,255,255,' + hi + ')');
    grad.addColorStop(0.56, 'rgba(255,255,255,' + hi + ')');
    grad.addColorStop(0.86, 'rgba(0,0,0,' + mid + ')');
    grad.addColorStop(1.00, 'rgba(0,0,0,' + edge + ')');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    // LRU-ish cap: prevent unbounded cache growth on resize spam.
    if (_STRIP_CACHE.size > 48) {
      var firstKey = _STRIP_CACHE.keys().next().value;
      _STRIP_CACHE.delete(firstKey);
    }
    _STRIP_CACHE.set(key, off);
    return off;
  }

  /* ── Fullscreen artwork viewer ──────────────────────────────────────
   * Opens when the user pinches outward on the 3D preview. Shows just
   * the artwork (no frame, no depth) so the customer can see the piece
   * in detail. Inside the viewer:
   *
   *   • Pinch zoom         — 1x to 4x (pinch-in clamps at 1x, snaps centered)
   *   • Wheel zoom         — desktop, centred on cursor
   *   • Double-tap / dbl-click — toggles between 1x and 2.5x
   *   • Drag (1-finger)    — tilts the 3D mockup (same as gallery view)
   *   • X button (top-right, safe-area aware) closes
   *   • Escape key closes
   *
   * Single instance, lazy-built. Reuses its DOM between opens so we
   * don't pay the create/teardown cost on repeated pinch-in/outs.
   * ──────────────────────────────────────────────────────────────── */
  var _fsStylesInjected = false;
  function injectFullscreenStyles() {
    if (_fsStylesInjected) return;
    _fsStylesInjected = true;
    var style = document.createElement('style');
    style.textContent =
      '.live-mockup-fs{' +
        'position:fixed;inset:0;background:rgba(15,15,15,0.96);' +
        'z-index:999999;overflow:hidden;opacity:0;' +
        'transition:opacity 200ms ease;' +
        '-webkit-user-select:none;user-select:none;' +
        'touch-action:none;' +
      '}' +
      '.live-mockup-fs[data-open="1"]{opacity:1}' +
      /* Stage holds the live-mockup while in fullscreen. Absolutely
         positioned to fill the viewport, it applies the pinch-zoom /
         pan transform on top of whatever 3D rotation the mockup itself
         is doing. transform-origin: 0 0 makes the zoom math around the
         pointer midpoint straightforward. */
      '.live-mockup-fs__stage{' +
        'position:absolute;top:0;left:0;' +
        'width:100vw;height:100vh;' +
        'width:100dvw;height:100dvh;' +
        'transform-origin:0 0;will-change:transform;' +
      '}' +
      /* While inside the fullscreen stage, the live-mockup keeps
         filling its parent (inset:0) but the drag hint pill inside
         the overlay should hide — we have a dedicated hint for FS. */
      '.live-mockup-fs__stage .live-mockup-hint{display:none}' +
      '.live-mockup-fs__close{' +
        'position:fixed;' +
        'top:calc(env(safe-area-inset-top,0px) + 16px);' +
        'right:calc(env(safe-area-inset-right,0px) + 16px);' +
        'width:40px;height:40px;border-radius:50%;padding:0;' +
        'background:rgba(255,255,255,0.16);' +
        'border:1px solid rgba(255,255,255,0.24);' +
        'color:#fff;cursor:pointer;z-index:2;' +
        'display:flex;align-items:center;justify-content:center;' +
        '-webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);' +
        'touch-action:manipulation;transition:background 0.15s ease;' +
      '}' +
      '.live-mockup-fs__close:hover,.live-mockup-fs__close:active{' +
        'background:rgba(255,255,255,0.30);' +
      '}' +
      '.live-mockup-fs__close svg{width:18px;height:18px;display:block}' +
      '.live-mockup-fs__hint{' +
        'position:fixed;left:50%;' +
        'bottom:calc(env(safe-area-inset-bottom,0px) + 22px);' +
        'transform:translateX(-50%);' +
        'padding:6px 12px;border-radius:999px;' +
        'background:rgba(20,20,20,0.55);color:#fff;' +
        'font-size:12px;font-weight:500;letter-spacing:0.01em;line-height:1;' +
        'white-space:nowrap;pointer-events:none;z-index:2;' +
        '-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px);' +
        'opacity:0;transition:opacity 220ms ease;' +
      '}' +
      '.live-mockup-fs[data-open="1"] .live-mockup-fs__hint{opacity:0.85}' +
      '.live-mockup-fs[data-interacted="1"] .live-mockup-fs__hint{opacity:0}';
    document.head.appendChild(style);
  }

  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

  var FS_MIN_ZOOM = 1;         // can't zoom below the default 1:1 view
  var FS_MAX_ZOOM = 4;         // cap — further pinch stops scaling
  var FS_DBLTAP_ZOOM = 2;      // level toggled by double-tap

  /* Fullscreen host — moves the real <live-mockup> element into an
     overlay-sized stage so every interaction (tilt, frame change,
     variant swap) keeps working on the same element. Layers its own
     pinch-zoom / pan transform on the *stage wrapper* so the mockup's
     internal 3D transforms stay untouched. */
  function LiveMockupFullscreen(mockup) {
    injectFullscreenStyles();
    this.mockup = mockup;
    this.originalParent = null;
    this.originalNextSibling = null;

    this.root = document.createElement('div');
    this.root.className = 'live-mockup-fs';

    this.stage = document.createElement('div');
    this.stage.className = 'live-mockup-fs__stage';

    this.closeBtn = document.createElement('button');
    this.closeBtn.type = 'button';
    this.closeBtn.className = 'live-mockup-fs__close';
    this.closeBtn.setAttribute('aria-label', 'Close fullscreen preview');
    this.closeBtn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ' +
      'aria-hidden="true" focusable="false">' +
      '<path d="M18 6 6 18"/><path d="M6 6l12 12"/></svg>';

    this.hint = document.createElement('div');
    this.hint.className = 'live-mockup-fs__hint';
    this.hint.textContent = 'Drag to tilt · Pinch to zoom';

    this.root.appendChild(this.stage);
    this.root.appendChild(this.closeBtn);
    this.root.appendChild(this.hint);

    // Zoom / pan transform (applied to the stage)
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;

    // Pinch state — populated by the mockup's pointer tracker, which
    // is the one actually receiving pointerdown/move/up. We don't bind
    // our own pointer handlers on the stage because the mockup already
    // captures them for its tilt/drag logic.
    this.pinchStartDist = 0;
    this.pinchStartScale = 1;
    this.pinchCenter = null;
    this._rafPending = false;

    this._onKey = this._onKey.bind(this);
    this._onWheel = this._onWheel.bind(this);
    this._onDblClick = this._onDblClick.bind(this);

    var self = this;
    this.closeBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      e.preventDefault();
      self.close();
    });
    // Wheel zoom (desktop) — on the root so it catches cursor anywhere
    // inside the overlay, not just over the mockup itself.
    this.root.addEventListener('wheel', this._onWheel, { passive: false });
    // Double-click on the backdrop (but not the mockup) recentres and
    // resets zoom. The mockup's own dblclick resets tilt, so this is
    // scoped to the root to avoid double-handling.
    this.root.addEventListener('dblclick', this._onDblClick);
  }

  LiveMockupFullscreen.prototype.open = function () {
    var self = this;
    var mockup = this.mockup;

    // Remember where the mockup came from so we can put it back on close.
    this.originalParent = mockup.parentNode;
    this.originalNextSibling = mockup.nextSibling;

    document.body.appendChild(this.root);
    document.body.style.overflow = 'hidden';
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this._apply();

    // Physically move the element. Same DOM node, same canvases, same
    // state — no flicker, no re-download. The live-mockup overlay's
    // own `inset: 0` makes it fill the stage automatically.
    this.stage.appendChild(mockup);
    mockup._isFullscreen = true;

    document.addEventListener('keydown', this._onKey);

    // Force a re-render at the larger parent size so the canvas is
    // crisp at fullscreen resolution — the DPR cap still applies, so
    // this just means we paint at the full viewport's worth of pixels
    // instead of the ~400px gallery slot's.
    requestAnimationFrame(function () {
      if (mockup._lastStateKey) mockup._lastStateKey = null;
      if (typeof mockup._scheduleRender === 'function') mockup._scheduleRender();
      self.root.setAttribute('data-open', '1');
    });
  };

  LiveMockupFullscreen.prototype.close = function () {
    if (!this.root.isConnected) return;
    var self = this;
    var mockup = this.mockup;

    this.root.removeAttribute('data-open');
    this.root.removeAttribute('data-interacted');
    document.removeEventListener('keydown', this._onKey);

    // Return the mockup to its original spot in the gallery slot.
    mockup._isFullscreen = false;
    if (this.originalParent) {
      if (this.originalNextSibling && this.originalNextSibling.parentNode === this.originalParent) {
        this.originalParent.insertBefore(mockup, this.originalNextSibling);
      } else {
        this.originalParent.appendChild(mockup);
      }
    }

    // Re-raster at the smaller gallery size.
    if (mockup._lastStateKey) mockup._lastStateKey = null;
    if (typeof mockup._scheduleRender === 'function') mockup._scheduleRender();

    document.body.style.overflow = '';

    setTimeout(function () {
      if (self.root.parentNode) self.root.parentNode.removeChild(self.root);
      self.pinchStartDist = 0;
      self.pinchCenter = null;
    }, 220);
  };

  /* Entry points called from the mockup's shared pointer tracker ---
     the mockup owns pointerdown/move/up (for tilt), so when it sees a
     second pointer it forwards the pinch state here. */
  LiveMockupFullscreen.prototype.onPinchStart = function (pointersMap) {
    var arr = Array.from(pointersMap.values());
    if (arr.length < 2) return;
    this.pinchStartDist = Math.hypot(arr[0].x - arr[1].x, arr[0].y - arr[1].y) || 1;
    this.pinchStartScale = this.scale;
    var cx = (arr[0].x + arr[1].x) / 2;
    var cy = (arr[0].y + arr[1].y) / 2;
    this.pinchCenter = { x: cx, y: cy, tx: this.tx, ty: this.ty };
  };

  LiveMockupFullscreen.prototype.onPinchMove = function (pointersMap) {
    if (!this.pinchCenter || this.pinchStartDist <= 0) return;
    var arr = Array.from(pointersMap.values());
    if (arr.length < 2) return;
    var dist = Math.hypot(arr[0].x - arr[1].x, arr[0].y - arr[1].y);
    var ratio = dist / this.pinchStartDist;
    var newScale = clamp(this.pinchStartScale * ratio, FS_MIN_ZOOM, FS_MAX_ZOOM);
    // Keep the initial pinch midpoint fixed in viewport space as we scale.
    var k = newScale / this.pinchStartScale;
    this.tx = this.pinchCenter.x - k * (this.pinchCenter.x - this.pinchCenter.tx);
    this.ty = this.pinchCenter.y - k * (this.pinchCenter.y - this.pinchCenter.ty);
    this.scale = newScale;
    // When fully zoomed out, snap back to centered — without this the
    // mockup drifts off-centre because the pinch-center math preserves
    // the finger midpoint as the transform origin. At 1:1 we want the
    // stage filling the viewport exactly like it does on open.
    if (this.scale <= FS_MIN_ZOOM) { this.tx = 0; this.ty = 0; }
    this._apply();
    this._markInteracted();
    // Pinch-in no longer auto-dismisses fullscreen — scale clamps at
    // FS_MIN_ZOOM (1:1). Customer closes via the X button or Escape.
  };

  LiveMockupFullscreen.prototype.onPinchEnd = function () {
    this.pinchStartDist = 0;
    this.pinchCenter = null;
  };

  LiveMockupFullscreen.prototype._onKey = function (e) {
    if (e.key === 'Escape') this.close();
  };

  LiveMockupFullscreen.prototype._onWheel = function (e) {
    e.preventDefault();
    var delta = -e.deltaY * 0.0025;
    var newScale = clamp(this.scale * (1 + delta), FS_MIN_ZOOM, FS_MAX_ZOOM);
    var k = newScale / this.scale;
    this.tx = e.clientX - k * (e.clientX - this.tx);
    this.ty = e.clientY - k * (e.clientY - this.ty);
    this.scale = newScale;
    // Re-centre when fully zoomed out (same rationale as pinch).
    if (this.scale <= FS_MIN_ZOOM) { this.tx = 0; this.ty = 0; }
    this._apply();
    this._markInteracted();
    // No auto-close on zoom-out — customer uses the X button / Escape.
  };

  LiveMockupFullscreen.prototype._onDblClick = function (e) {
    // Only react if the double-click landed on the backdrop, not on
    // the mockup itself (which uses dblclick to reset tilt).
    if (e.target !== this.root && e.target !== this.stage) return;
    var target = this.scale > 1.2 ? 1 : FS_DBLTAP_ZOOM;
    var k = target / this.scale;
    this.tx = e.clientX - k * (e.clientX - this.tx);
    this.ty = e.clientY - k * (e.clientY - this.ty);
    this.scale = target;
    if (target === 1) { this.tx = 0; this.ty = 0; }
    this._apply();
    this._markInteracted();
  };

  LiveMockupFullscreen.prototype._apply = function () {
    if (this._rafPending) return;
    this._rafPending = true;
    var self = this;
    requestAnimationFrame(function () {
      self._rafPending = false;
      self.stage.style.transform =
        'translate(' + self.tx + 'px, ' + self.ty + 'px) scale(' + self.scale + ')';
    });
  };

  LiveMockupFullscreen.prototype._markInteracted = function () {
    this.root.setAttribute('data-interacted', '1');
  };

  class LiveMockup extends HTMLElement {
    constructor() {
      super();
      this._booted = false;
      this._images = { primary: null, alt: null };
      this._textures = {};
      this._textureLoads = {};
      this._rafPending = false;

      this._onDocChange = this._onDocChange.bind(this);
      this._onVariantEvent = this._onVariantEvent.bind(this);
      this._onCustomPreviewEvent = this._onCustomPreviewEvent.bind(this);
      this._render = this._render.bind(this);
      this._scheduleRender = this._scheduleRender.bind(this);
    }

    connectedCallback() {
      if (this._booted) {
        if (this._assetsReady) {
          this._bind();
          this._bindDrag();
          this._scheduleRender();
          this.setAttribute('data-ready', '1');
        }
        return;
      }
      this._booted = true;

      var dataEl = this.querySelector('.live-mockup-data');
      if (!dataEl) return;
      try { this._data = JSON.parse(dataEl.textContent); }
      catch (e) { return; }

      this._isCustomPreview = this.dataset.custom === '1';
      this._primarySpec = {
        url: this.dataset.primaryUrl || '',
        w: parseFloat(this.dataset.primaryW) || 0,
        h: parseFloat(this.dataset.primaryH) || 0
      };
      this._altSpec = {
        url: this.dataset.altUrl || '',
        w: parseFloat(this.dataset.altW) || 0,
        h: parseFloat(this.dataset.altH) || 0
      };
      if (!this._primarySpec.url && !this._altSpec.url && !this._isCustomPreview) return;

      this._buildDom();
      this._attachToGallery();

      var self = this;
      Promise.all([
        loadImage(this._primarySpec.url).then(function (i) { self._images.primary = i; }),
        loadImage(this._altSpec.url).then(function (i) { self._images.alt = i; }),
        this._loadTexture(frameKeyFromValue(this._parseVariant(this._getSelectedVariant()).frame))
      ]).then(function () {
        self._assetsReady = true;
        if (!self.isConnected) return;
        self._bind();
        self._bindDrag();
        self._render();
        self.setAttribute('data-ready', '1');
        // Keep the Safari first-paint correction off other browsers.
        if (/iPhone|iPad|iPod/.test(navigator.userAgent) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
          self._scheduleSettleRerender();
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener('change', this._onDocChange, true);
      document.removeEventListener('variant:change', this._onVariantEvent);
      document.removeEventListener('on:variant:change', this._onVariantEvent);
      this.removeEventListener('live-mockup:update', this._onCustomPreviewEvent);
      window.removeEventListener('resize', this._scheduleRender);
      if (this._resizeObserver) this._resizeObserver.disconnect();
      this._bound = false;
      cancelAnimationFrame(this._renderRaf);
      this._rafPending = false;
    }

    _buildDom() {
      // Structure:
      //   <live-mockup.overlay> (pointer target, flex-centers the shadow)
      //     <div.shadow>        (perspective + drop-shadow)
      //       <div.scene>       (preserve-3d + rotateX/Y from drag)
      //         <canvas.face>   (front)
      //         <canvas.side--left>
      //         <canvas.side--right>
      //         <canvas.side--top>
      //         <canvas.side--bottom>
      //
      // The shadow wrapper carries the filter so it can flatten the
      // scene's 2D composite WITHOUT flattening the scene's internal
      // 3D children — preserve-3d stays intact inside the scene.
      this._shadow = document.createElement('div');
      this._shadow.className = 'live-mockup-shadow';

      this._scene = document.createElement('div');
      this._scene.className = 'live-mockup-scene';

      this._backstop = document.createElement('div');
      this._backstop.className = 'live-mockup-backstop';

      this._frontCanvas = document.createElement('canvas');
      this._frontCanvas.className = 'live-mockup-face';

      this._leftCanvas = document.createElement('canvas');
      this._leftCanvas.className = 'live-mockup-side live-mockup-side--left';

      this._rightCanvas = document.createElement('canvas');
      this._rightCanvas.className = 'live-mockup-side live-mockup-side--right';

      this._topCanvas = document.createElement('canvas');
      this._topCanvas.className = 'live-mockup-side live-mockup-side--top';

      this._bottomCanvas = document.createElement('canvas');
      this._bottomCanvas.className = 'live-mockup-side live-mockup-side--bottom';

      this._scene.appendChild(this._backstop);
      this._scene.appendChild(this._frontCanvas);
      this._scene.appendChild(this._leftCanvas);
      this._scene.appendChild(this._rightCanvas);
      this._scene.appendChild(this._topCanvas);
      this._bottomCanvas && this._scene.appendChild(this._bottomCanvas);

      this._shadow.appendChild(this._scene);
      this.appendChild(this._shadow);

      // Interactivity hint — a small pill near the bottom that tells
      // the user the preview is draggable. Sits outside the 3D scene
      // so it doesn't get tilted along with the artwork. Hidden on
      // first drag (see _bindDrag) and stays hidden afterwards.
      this._hint = document.createElement('div');
      this._hint.className = 'live-mockup-hint';
      this._hint.setAttribute('aria-hidden', 'true');
      this._hint.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ' +
        'aria-hidden="true" focusable="false">' +
        '<path d="M12 4a8 8 0 1 0 8 8"/>' +
        '<polyline points="20 4 20 8 16 8"/>' +
        '<path d="M20 4 L14 10"/>' +
        '</svg>' +
        '<span>Drag to rotate</span>';
      this.appendChild(this._hint);

      // Default starting tilt: gallery products start angled, while the
      // custom upload configurator starts straight-on for the guided tabs.
      this._rx = this._isCustomPreview ? 0 : 4;
      this._ry = this._isCustomPreview ? 0 : 16;
      this._scene.style.setProperty('--lm-rx', this._rx + 'deg');
      this._scene.style.setProperty('--lm-ry', this._ry + 'deg');
      this._scene.style.transform = 'rotateX(' + this._rx + 'deg) rotateY(' + this._ry + 'deg)';
    }

    _attachToGallery() {
      var section = this.closest('[id^="shopify-section-"]') || document;

      // Preferred path: product-gallery.liquid reserves a dedicated first
      // slot (.product-gallery__media--live-mockup-slot) with matching
      // thumbnail so product photos keep their original positions. The slot
      // is already marked `is-initial` in Liquid, so the carousel starts on
      // us without any JS coordination.
      var slot = section.querySelector('.product-gallery__media--live-mockup-slot');

      // Legacy fallback (older themes / sections without the reserved slot):
      // hijack the first product media slot so the mockup still renders.
      if (!slot) {
        slot = section.querySelector('.product-gallery__media:not([hidden])')
          || section.querySelector('.product-gallery__media');
        if (!slot) return;
        slot.classList.add('product-gallery__media--live-mockup');
      }

      if (getComputedStyle(slot).position === 'static') {
        slot.style.position = 'relative';
      }
      slot.appendChild(this);

      this._slot = slot;
      this._wireZoomButton();
    }

    /* Route the theme's mobile magnifier (.product-gallery__zoom-button,
       wrapped in <open-lightbox-button>) to open the interactive 3D
       fullscreen when the customer is sitting on our mockup slide.

       Strategy: intercept the `lightbox:open` event at <product-gallery>
       in the capture phase. The theme's ProductGallery constructor adds
       a bubble-phase listener for the same event — capture runs first,
       so stopImmediatePropagation() from here prevents PhotoSwipe.

       Why here (not on the button): several theme paths can dispatch
       `lightbox:open` (direct image double-tap, zoom button, etc.).
       Catching the event at the gallery covers them all. Events with an
       explicit detail.index come from image-cell clicks and can't
       target our slot (it has no <img>), so we let those through. */
    _wireZoomButton() {
      if (this._zoomBtnWired) return;
      var gallery = this.closest('product-gallery');
      if (!gallery) return;

      var self = this;
      var slot = this._slot;

      function isOurSlide(cell) {
        if (!cell || !cell.classList) return false;
        return cell === slot
          || cell.classList.contains('product-gallery__media--live-mockup-slot')
          || cell.classList.contains('product-gallery__media--live-mockup');
      }

      function getCarousel() {
        return gallery.querySelector('scroll-carousel.product-gallery__carousel')
          || gallery.querySelector('.product-gallery__carousel');
      }

      gallery.addEventListener('lightbox:open', function (e) {
        if (e.detail && typeof e.detail.index === 'number') return;

        var carousel = getCarousel();
        if (!carousel) return;
        var cell = carousel.selectedCell;
        if (!cell && carousel.cells) cell = carousel.cells[0];
        if (!isOurSlide(cell)) return; // not our slide — let PhotoSwipe handle it

        e.stopImmediatePropagation();
        self._openFullscreen();
      }, { capture: true });

      /* The theme's onMediaSettle switch has cases for image / video /
         model only — when our `live-mockup` media-type settles, nothing
         clears the `--hidden` class. So if the customer swiped from a
         video or model back to our slot, the zoom button stays invisible
         even though our handler is primed to catch its click. Force it
         visible whenever our slide settles. */
      var zoomBtn = gallery.querySelector('.product-gallery__zoom-button');
      if (zoomBtn) {
        var carousel = getCarousel();
        if (carousel) {
          carousel.addEventListener('carousel:settle', function (e) {
            var cell = (e && e.detail) ? e.detail.cell : carousel.selectedCell;
            if (isOurSlide(cell)) {
              zoomBtn.classList.remove('product-gallery__zoom-button--hidden');
            }
          });
          // Handle the initial state too — if our slot is the starting
          // cell, the settle event may have fired before we wired up.
          if (isOurSlide(carousel.selectedCell)) {
            zoomBtn.classList.remove('product-gallery__zoom-button--hidden');
          }
        }
      }

      this._zoomBtnWired = true;
    }

    _loadTexture(key) {
      var self = this;
      var tex = this._data.textures || {};
      if (!key || !tex[key]) return Promise.resolve();
      if (!this._textureLoads[key]) {
        this._textureLoads[key] = loadImage(tex[key]).then(function (img) {
          self._textures[key] = img;
        });
      }
      return this._textureLoads[key];
    }

    _bind() {
      if (this._bound) return;
      this._bound = true;
      document.addEventListener('change', this._onDocChange, true);
      document.addEventListener('variant:change', this._onVariantEvent);
      document.addEventListener('on:variant:change', this._onVariantEvent);
      this.addEventListener('live-mockup:update', this._onCustomPreviewEvent);
      if (window.ResizeObserver) {
        this._resizeObserver = new ResizeObserver(this._scheduleRender);
        this._resizeObserver.observe(this);
      }
      window.addEventListener('resize', this._scheduleRender, { passive: true });
    }

    _onDocChange(e) {
      var t = e.target;
      if (!t || !t.matches) return;
      if (!t.matches('input[type="radio"], select, input[type="hidden"][name="id"]')) return;
      var section = this.closest('[id^="shopify-section-"]');
      if (section && !section.contains(t)) return;
      this._scheduleRender();
    }

    _onVariantEvent() { this._scheduleRender(); }

    _onCustomPreviewEvent(e) {
      if (e && e.detail) this.setCustomPreview(e.detail);
    }

    setCustomPreview(detail) {
      if (!detail) return;

      if (detail.widthInches && detail.heightInches) {
        this._customSize = {
          w: parseFloat(detail.widthInches),
          h: parseFloat(detail.heightInches)
        };
      }

      if (detail.depthInches) {
        this._customDepth = parseFloat(detail.depthInches);
      }

      if (detail.view) {
        this.setCustomView(detail.view);
      }

      if (detail.imageUrl && detail.imageUrl !== this._customImageUrl) {
        var self = this;
        this._customImageUrl = detail.imageUrl;
        loadImage(detail.imageUrl).then(function (img) {
          if (!img) return;
          self._images.primary = img;
          self._images.alt = null;
          self._lastStateKey = null;
          self.setAttribute('data-ready', '1');
          self._scheduleRender();
        });
        return;
      }

      this._lastStateKey = null;
      this._scheduleRender();
    }

    setCustomView(view) {
      var nextView = view || 'front';
      var rx = 0;
      var ry = 0;

      if (nextView === 'edges') {
        rx = 4;
        ry = 28;
      } else if (nextView === 'room') {
        rx = 4;
        ry = 16;
      } else if (nextView === 'closeup') {
        rx = 0;
        ry = 0;
      }

      this.dataset.view = nextView;
      this._rx = rx;
      this._ry = ry;
      if (this._scene) {
        this._scene.style.transform = 'rotateX(' + rx + 'deg) rotateY(' + ry + 'deg)';
      }
    }

    _scheduleRender() {
      if (this._rafPending || !this.isConnected) return;
      this._rafPending = true;
      var self = this;
      this._renderRaf = requestAnimationFrame(function () {
        self._rafPending = false;
        self._render();
      });
    }

    /* Preserve Safari's first-paint raster correction after layout settles. */
    _scheduleSettleRerender() {
      var self = this;
      function force() {
        if (!self.isConnected) return;
        self._lastStateKey = null;
        self._render();
        self._kickRaster();
      }
      // Two frames out: lets the carousel / layout settle past first paint.
      requestAnimationFrame(function () { requestAnimationFrame(force); });
      // And again once everything (fonts, images, late layout) is fully loaded.
      if (document.readyState !== 'complete') {
        window.addEventListener('load', force, { once: true });
      }
    }

    /* Force WebKit to tear down and rebuild the 3D scene's GPU layer so it
       re-rasterizes at full resolution. This is the ONLY thing that reliably
       clears iOS Safari's low-res first-paint raster — and it's exactly what
       moving the element during pinch-to-fullscreen does (the user's "pinch
       fixes it" path). A synchronous display toggle destroys the composited
       layer (display:none) and rebuilds it on restore. Because both writes
       happen in the same task with no paint between them, there's no visible
       flash. The canvas bitmap content survives the toggle untouched. */
    _kickRaster() {
      var sh = this._shadow;
      if (!sh) return;
      var prev = sh.style.display;
      sh.style.display = 'none';
      // Reading a layout property flushes the style/layout change, committing
      // the display:none so the GPU layer is actually destroyed before we
      // restore it on the next line.
      void sh.offsetHeight;
      sh.style.display = prev || '';
    }

    _getSelectedVariant() {
      var section = this.closest('[id^="shopify-section-"]');
      var root = section || document;
      var form = root.querySelector('form[action*="/cart/add"]');
      var variantId = null;
      if (form) {
        var idInput = form.querySelector('input[name="id"]:checked, select[name="id"], input[name="id"]');
        if (idInput && idInput.value) variantId = parseInt(idInput.value, 10);
      }
      if (!variantId) return this._data.variants[0] || null;
      for (var i = 0; i < this._data.variants.length; i++) {
        if (this._data.variants[i].id === variantId) return this._data.variants[i];
      }
      return this._data.variants[0] || null;
    }

    _parseVariant(v) {
      if (!v) return { frame: null, size: null, depth: null };
      var names = (this._data.optionNames || []).map(function (n) {
        return (n || '').toString().toLowerCase();
      });
      var vals = [v.option1, v.option2, v.option3];
      var out = { frame: null, size: null, depth: null };
      for (var i = 0; i < names.length; i++) {
        var n = names[i], val = vals[i];
        if (!val) continue;
        if (n === 'frame' || n === 'frame style' || n === 'frame color') {
          out.frame = String(val);
        } else if (n === 'size' || n.indexOf('size') >= 0 || n === 'dimensions') {
          out.size = String(val);
        } else if (n === 'depth' || n.indexOf('depth') >= 0 || n.indexOf('profile') >= 0) {
          out.depth = String(val);
        }
      }
      return out;
    }

    /**
     * Pick the artwork image whose aspect ratio is closest (in log-space)
     * to the currently selected size. Handles the artwork_image_alt case
     * for 1×4 vs 1×3 vertical crops.
     */
    _pickArtwork(sizeDims) {
      var primary = this._images.primary;
      var alt = this._images.alt;
      if (primary && !alt) return primary;
      if (alt && !primary) return alt;
      if (!primary && !alt) return null;
      if (!sizeDims) return primary;

      // Size comes as {a, b} (whichever order the variant used).
      // The artwork image itself dictates orientation — so compare each
      // artwork's aspect ratio against BOTH orderings of the size.
      var targetA = sizeDims.a / sizeDims.b;      // a-horizontal
      var targetB = sizeDims.b / sizeDims.a;      // b-horizontal
      var pR = primary.naturalWidth / primary.naturalHeight;
      var aR = alt.naturalWidth / alt.naturalHeight;
      function diff(img, t) { return Math.abs(Math.log(img) - Math.log(t)); }
      var pBest = Math.min(diff(pR, targetA), diff(pR, targetB));
      var aBest = Math.min(diff(aR, targetA), diff(aR, targetB));
      return pBest <= aBest ? primary : alt;
    }

    /**
     * Align the parsed size numbers to the artwork image orientation.
     * Variant size strings like "20×30" don't carry orientation info —
     * the artwork image does.
     */
    _orientSize(sizeDims, artImg) {
      if (!sizeDims) return null;
      var min = Math.min(sizeDims.a, sizeDims.b);
      var max = Math.max(sizeDims.a, sizeDims.b);
      var isArtLandscape = artImg.naturalWidth >= artImg.naturalHeight;
      if (isArtLandscape) return { w: max, h: min };
      return { w: min, h: max };
    }

    _render() {
      if (!this.isConnected || !this._assetsReady) return;

      var variant = this._getSelectedVariant();
      var parsed = this._parseVariant(variant);
      var sizeDims = parseSize(parsed.size);

      var artImg = this._isCustomPreview && this._images.primary
        ? this._images.primary
        : this._pickArtwork(sizeDims);
      if (!artImg) return;

      var size = this._customSize || this._orientSize(sizeDims, artImg);
      if (!size) {
        // No parseable size — fall back to artwork aspect, assume 24" long side.
        var pw = artImg.naturalWidth, ph = artImg.naturalHeight;
        size = (pw >= ph) ? { w: 24, h: 24 * (ph / pw) }
          : { w: 24 * (pw / ph), h: 24 };
      }

      var frameKey = frameKeyFromValue(parsed.frame);
      if (frameKey && this._data.textures[frameKey] && this._textures[frameKey] === undefined) {
        this._loadTexture(frameKey).then(this._scheduleRender);
        return;
      }
      var hasFrame = !!(frameKey && this._textures[frameKey]);
      var frameInches = hasFrame ? (this._data.frameInches || 0.5) : 0;
      var revealInches = hasFrame ? (this._data.revealInches || 0.25) : 0;
      var depthInches = this._customDepth || parseDepth(parsed.depth) || this._data.depthInches || 1.5;

      var overlay = this.getBoundingClientRect();
      // ResizeObserver schedules the next draw when a hidden slide reappears.
      if (overlay.width < 20 || overlay.height < 20) return;

      // Short-circuit: if nothing visible has changed since the last render,
      // skip the whole canvas re-raster. This is what keeps rapid frame
      // clicks from causing visible lag — we only rebuild the strips when
      // something the user can actually see has moved.
      var stateKey = [
        frameKey || '-',
        Math.round(overlay.width), Math.round(overlay.height),
        size.w, size.h,
        depthInches,
        artImg.src || '-'
      ].join('|');
      if (stateKey === this._lastStateKey) return;
      this._lastStateKey = stateKey;

      var maxW = overlay.width * 0.86;
      var maxH = overlay.height * 0.90;

      // Total real-world footprint = artwork + (frame + reveal) on each side
      var totalPad = frameInches + revealInches;
      var totalW = size.w + totalPad * 2;
      var totalH = size.h + totalPad * 2;
      var scale = Math.min(maxW / totalW, maxH / totalH);

      var frontW = totalW * scale;
      var frontH = totalH * scale;
      var framePx = frameInches * scale;
      var revealPx = revealInches * scale;
      var artPxW = size.w * scale;
      var artPxH = size.h * scale;
      var depthPx = Math.max(8, depthInches * scale);

      // Physical dimensions (inches) needed for the unframed side wrap:
      // the slice taken from the artwork to wrap a side equals the depth,
      // so the slice fraction = depth / artwork side length (not a fixed
      // percentage, which was causing the stretch the user noticed).
      var artInW = size.w;
      var artInH = size.h;

      // Render each face's pixel content
      this._drawFront(artImg, frameKey, frontW, frontH, framePx, revealPx, artPxW, artPxH);
      this._drawSide(this._leftCanvas, artImg, frameKey, depthPx, frontH, 'left', depthInches, artInW, artInH);
      this._drawSide(this._rightCanvas, artImg, frameKey, depthPx, frontH, 'right', depthInches, artInW, artInH);
      this._drawSide(this._topCanvas, artImg, frameKey, frontW, depthPx, 'top', depthInches, artInW, artInH);
      this._drawSide(this._bottomCanvas, artImg, frameKey, frontW, depthPx, 'bottom', depthInches, artInW, artInH);

      // Scene is the front face's CSS size (W × H). Sides are positioned
      // in 3D around this rectangle.
      this._scene.style.width = frontW + 'px';
      this._scene.style.height = frontH + 'px';

      var halfD = depthPx / 2;

      // A one-pixel overlap seals the antialiased join on continuous canvas
      // wraps, where a gap would expose the dark backing between faces.
      var ov = hasFrame ? FACE_OVERLAP : 1;

      // Inset the stretched-canvas backing so it cannot peek past the wrap's
      // antialiased rear edges. Framed backing still fills the rear bounds.
      var backInset = hasFrame ? 0 : EDGE_PAD * 2;
      var crackColor = '#111';
      if (frameKey === 'white') crackColor = '#e3e3e3';
      if (frameKey === 'natural') crackColor = '#a88c6f';
      if (frameKey === 'oak') crackColor = '#8c7053';
      if (frameKey === 'walnut') crackColor = '#302015';

      this._backstop.style.width = (frontW - backInset * 2) + 'px';
      this._backstop.style.height = (frontH - backInset * 2) + 'px';
      this._backstop.style.backgroundColor = crackColor;
      this._backstop.style.transform =
        'translate(' + backInset + 'px, ' + backInset + 'px) translateZ(' + (-halfD) + 'px)';

      // FRONT — pushed forward by half the depth. The transparent gutter
      // is offset by _sizeCanvas so the painted face still meets the sides.
      this._frontCanvas.style.width = (frontW + ov * 2 + EDGE_PAD * 2) + 'px';
      this._frontCanvas.style.height = (frontH + ov * 2 + EDGE_PAD * 2) + 'px';
      this._frontCanvas.style.transform =
        'translate(' + (-ov) + 'px, ' + (-ov) + 'px) translateZ(' + halfD + 'px)';

      // LEFT side (D × H). After rotateY(-90) it lies in the YZ plane,
      // centre at x=D/2. translateX(-D/2) brings it flush to the scene's
      // left edge (x=0). Z spans [-D/2, +D/2] so it meets the front at
      // (x=0, z=+D/2).
      this._leftCanvas.style.width = (depthPx + EDGE_PAD * 2) + 'px';
      this._leftCanvas.style.height = (frontH + EDGE_PAD * 2) + 'px';
      this._leftCanvas.style.transform =
        'translateX(' + (-halfD) + 'px) rotateY(-90deg)';

      // RIGHT side (D × H). rotateY(+90) + translateX(W - D/2).
      this._rightCanvas.style.width = (depthPx + EDGE_PAD * 2) + 'px';
      this._rightCanvas.style.height = (frontH + EDGE_PAD * 2) + 'px';
      this._rightCanvas.style.transform =
        'translateX(' + (frontW - halfD) + 'px) rotateY(90deg)';

      // TOP side (W × D). rotateX(+90) + translateY(-D/2) puts it along
      // scene's top edge, pointing up.
      this._topCanvas.style.width = (frontW + EDGE_PAD * 2) + 'px';
      this._topCanvas.style.height = (depthPx + EDGE_PAD * 2) + 'px';
      this._topCanvas.style.transform =
        'translateY(' + (-halfD) + 'px) rotateX(90deg)';

      // BOTTOM side (W × D). rotateX(-90) + translateY(H - D/2).
      this._bottomCanvas.style.width = (frontW + EDGE_PAD * 2) + 'px';
      this._bottomCanvas.style.height = (depthPx + EDGE_PAD * 2) + 'px';
      this._bottomCanvas.style.transform =
        'translateY(' + (frontH - halfD) + 'px) rotateX(-90deg)';
    }

    _sizeCanvas(cvs, w, h, dpr) {
      var r = dpr || DPR;
      cvs.width = Math.max(1, Math.ceil((w + EDGE_PAD * 2) * r));
      cvs.height = Math.max(1, Math.ceil((h + EDGE_PAD * 2) * r));
      cvs.style.width = (w + EDGE_PAD * 2) + 'px';
      cvs.style.height = (h + EDGE_PAD * 2) + 'px';
      cvs.style.margin = -EDGE_PAD + 'px';
      var ctx = cvs.getContext('2d');
      // High-quality smoothing when the 2000-2400px source is downsampled
      // into the canvas buffer. Default ("low") in Chromium uses a fast
      // bilinear filter that leaves detailed artwork reading as soft;
      // "high" uses Lanczos-like filtering. Cost is per variant-change
      // only (drag rotation is CSS transform, not a re-raster) so the
      // 2-3× slower draw is unnoticeable in practice.
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      var sx = cvs.width / (w + EDGE_PAD * 2);
      var sy = cvs.height / (h + EDGE_PAD * 2);
      ctx.setTransform(sx, 0, 0, sy, EDGE_PAD * sx, EDGE_PAD * sy);
      // Keep shadows and strokes out of the transparent sampling gutter.
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.clip();
      return ctx;
    }

    _drawFront(artImg, frameKey, W, H, framePx, revealPx, artW, artH) {
      var ctx = this._sizeCanvas(this._frontCanvas, W, H);
      ctx.clearRect(0, 0, W, H);

      if (frameKey && this._textures[frameKey]) {
        // -------- FLOATING FRAME --------
        // 1) Dark reveal fills the whole front; frame paints over the outer
        //    band, artwork sits inset inside the reveal ring.
        // We use a darker version of the frame material for the inner gap
        let revealColor = '#0a0a0a'; // Default to black for 'gold' and 'black'
        if (frameKey === 'white') revealColor = '#858585'; // Darkened white reveal
        if (frameKey === 'oak') revealColor = '#5c4530';
        if (frameKey === 'walnut') revealColor = '#24160f';
        if (frameKey === 'natural') revealColor = '#806e57';

        ctx.fillStyle = revealColor;
        ctx.fillRect(0, 0, W, H);

        var innerX = framePx;
        var innerY = framePx;
        var innerW = W - 2 * framePx;
        var innerH = H - 2 * framePx;
        var artX = framePx + revealPx;
        var artY = framePx + revealPx;

        // 2) Artwork — drawn FIRST so its shadow falls into the dark reveal,
        //    and any spillover gets covered cleanly by the frame molding.
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.4)';
        ctx.shadowBlur = Math.max(3, revealPx * 0.85);
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = Math.max(2, revealPx * 0.35);
        ctx.drawImage(artImg, artX, artY, artW, artH);
        ctx.restore();

        // 3) Crisp dark line around the artwork edge so the canvas rim
        //    separates cleanly from the reveal.
        ctx.save();
        ctx.strokeStyle = 'rgba(0,0,0,0.32)';
        ctx.lineWidth = 1;
        ctx.strokeRect(artX + 0.5, artY + 0.5, artW - 1, artH - 1);
        ctx.restore();

        // 4) Four mitred frame strips with stretched texture + profile shading
        this._paintFrame(ctx, this._textures[frameKey], W, H, framePx, frameKey);

        // 5) Inner highlight along the frame's inside edge (light catches
        //    the top of the molding where it meets the reveal).
        ctx.save();
        ctx.strokeStyle = 'rgba(255,255,255,0.12)';
        ctx.lineWidth = 1;
        ctx.strokeRect(innerX + 0.5, innerY + 0.5, innerW - 1, innerH - 1);
        ctx.restore();
      } else {
        // -------- STRETCHED CANVAS — no frame --------
        ctx.drawImage(artImg, 0, 0, W, H);
      }
    }

    /**
     * Paint the four frame strips using pre-rendered textures (with grain
     * rotated 90° for left/right). Clipped to mitred trapezoids so the
     * corners look like a real mitred frame.
     */
    _paintFrame(ctx, tex, W, H, t, frameKey) {
      var strips = [
        // TOP
        {
          clip: [[0, 0], [W, 0], [W - t, t], [t, t]],
          rect: [0, 0, W, t],
          strip: makeFrameStrip(tex, W, t, false, frameKey)
        },
        // BOTTOM
        {
          clip: [[0, H], [t, H - t], [W - t, H - t], [W, H]],
          rect: [0, H - t, W, t],
          strip: makeFrameStrip(tex, W, t, false, frameKey)
        },
        // LEFT
        {
          clip: [[0, 0], [t, t], [t, H - t], [0, H]],
          rect: [0, 0, t, H],
          strip: makeFrameStrip(tex, H, t, true, frameKey)
        },
        // RIGHT
        {
          clip: [[W, 0], [W, H], [W - t, H - t], [W - t, t]],
          rect: [W - t, 0, t, H],
          strip: makeFrameStrip(tex, H, t, true, frameKey)
        }
      ];

      strips.forEach(function (s) {
        ctx.save();
        ctx.beginPath();
        s.clip.forEach(function (p, i) {
          if (i === 0) ctx.moveTo(p[0], p[1]);
          else ctx.lineTo(p[0], p[1]);
        });
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(s.strip, s.rect[0], s.rect[1], s.rect[2], s.rect[3]);
        ctx.restore();
      });

      // Mitre seam lines — the diagonal hairlines where two frame pieces
      // meet at each corner. Kept faint and thin so they read as seams
      // rather than drawn-on lines.
      ctx.save();
      ctx.strokeStyle = 'rgba(0,0,0,0.18)';
      ctx.lineWidth = 0.4;
      ctx.beginPath();
      ctx.moveTo(0, 0); ctx.lineTo(t, t);
      ctx.moveTo(W, 0); ctx.lineTo(W - t, t);
      ctx.moveTo(0, H); ctx.lineTo(t, H - t);
      ctx.moveTo(W, H); ctx.lineTo(W - t, H - t);
      ctx.stroke();
      ctx.restore();

      // Outer bevel highlight
      ctx.save();
      ctx.strokeStyle = 'rgba(255,255,255,0.08)';
      ctx.lineWidth = 0.5;
      ctx.strokeRect(0.5, 0.5, W - 1, H - 1);
      ctx.restore();
    }

    /**
     * Draw one of the four side faces of the cuboid.
     *
     * For framed pieces, the side is the edge of the moulding — filled
     * with the frame texture. Left/right use the texture rotated 90° so
     * the grain runs along the frame length; top/bottom use the grain
     * in its natural horizontal direction.
     *
     * For stretched canvas (no frame), each side mirrors a narrow slice
     * of the artwork's corresponding edge, so the wrap-around reads like
     * a real gallery-wrapped canvas.
     *
     * The canvas is given its full depthPx of internal resolution; CSS
     * 3D rotation foreshortens it naturally at view time. Framed sides
     * get a subtle crease gradient; stretched artwork wraps without it.
     */
    _drawSide(cvs, artImg, frameKey, W, H, side, depthIn, artInW, artInH) {
      // Side faces are narrow strips in the final render — drop DPR
      // to 1.5 max (from 2) for a free perf win.
      var ctx = this._sizeCanvas(cvs, W, H, SIDE_DPR);
      ctx.clearRect(0, 0, W, H);

      var vertical = (side === 'left' || side === 'right');

      if (frameKey && this._textures[frameKey]) {
        // Framed side — stretched texture with grain running along the
        // frame direction.
        var strip = makeFrameStrip(
          this._textures[frameKey],
          vertical ? H : W,   // length (along the frame)
          vertical ? W : H,   // thickness
          vertical,
          frameKey            // drives the light/dark gradient branch
        );
        ctx.drawImage(strip, 0, 0, W, H);

        // AO gradient at the seam side so the crease reads.
        // 'seam' is the edge of THIS face that meets the front face.
        //   left  → seam on the RIGHT of this canvas
        //   right → seam on the LEFT of this canvas
        //   top   → seam on the BOTTOM
        //   bottom→ seam on the TOP
        var isLightFrame = !!(frameKey && _LIGHT_FRAMES[frameKey]);
        var gradAlpha = isLightFrame ? 0.06 : 0.18;
        var grad = this._seamGradient(ctx, side, W, H, gradAlpha);
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, W, H);
      } else {
        // Stretched canvas side — mirror the artwork's corresponding
        // edge. The slice width in artwork pixels matches the physical
        // depth of the canvas: a 1.5" deep canvas over a 24" wide piece
        // consumes a 1.5/24 = 6.25% strip. The old fixed 4% caused the
        // "stretched edge" look on bigger pieces because it sampled too
        // little of the artwork and then stretched it across the full
        // depth face.
        var natW = artImg.naturalWidth;
        var natH = artImg.naturalHeight;
        var dIn = depthIn || 1.5;
        var fracW = Math.min(0.5, dIn / Math.max(1, artInW || 24));
        var fracH = Math.min(0.5, dIn / Math.max(1, artInH || 24));

        // For all four sides, we mirror the edge slice so that the
        // artwork's TRUE outer column/row lands at the side face's NEAR
        // edge (the one meeting the front face) and the deeper-interior
        // pixels trail off toward the back. Without the flip the slice
        // reads "inside-out" — the near edge shows interior pixels that
        // look like a duplicated copy of the front's edge, while the
        // back edge shows the actual outermost artwork pixel. That was
        // the stretched/duplicated look the earlier version had on the
        // right and bottom faces.
        ctx.save();
        if (side === 'left') {
          var slW = Math.max(2, Math.floor(natW * fracW));
          ctx.translate(W, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(artImg, 0, 0, slW, natH, 0, 0, W, H);
        } else if (side === 'right') {
          var srW = Math.max(2, Math.floor(natW * fracW));
          ctx.translate(W, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(artImg, natW - srW, 0, srW, natH, 0, 0, W, H);
        } else if (side === 'top') {
          var stH = Math.max(2, Math.floor(natH * fracH));
          ctx.translate(0, H);
          ctx.scale(1, -1);
          ctx.drawImage(artImg, 0, 0, natW, stH, 0, 0, W, H);
        } else {
          // bottom
          var sbH = Math.max(2, Math.floor(natH * fracH));
          ctx.translate(0, H);
          ctx.scale(1, -1);
          ctx.drawImage(artImg, 0, natH - sbH, natW, sbH, 0, 0, W, H);
        }
        ctx.restore();
      }
    }

    /** Build a linear gradient fading to rgba(0,0,0,alpha) at the edge
        of this face that meets the front face. */
    _seamGradient(ctx, side, W, H, alpha) {
      var g;
      if (side === 'left') {
        g = ctx.createLinearGradient(0, 0, W, 0);
        g.addColorStop(0, 'rgba(0,0,0,0)');
        g.addColorStop(1, 'rgba(0,0,0,' + alpha + ')');
      } else if (side === 'right') {
        g = ctx.createLinearGradient(0, 0, W, 0);
        g.addColorStop(0, 'rgba(0,0,0,' + alpha + ')');
        g.addColorStop(1, 'rgba(0,0,0,0)');
      } else if (side === 'top') {
        g = ctx.createLinearGradient(0, 0, 0, H);
        g.addColorStop(0, 'rgba(0,0,0,0)');
        g.addColorStop(1, 'rgba(0,0,0,' + alpha + ')');
      } else {
        g = ctx.createLinearGradient(0, 0, 0, H);
        g.addColorStop(0, 'rgba(0,0,0,' + alpha + ')');
        g.addColorStop(1, 'rgba(0,0,0,0)');
      }
      return g;
    }

    /* ── Pointer drag → rotateX/rotateY + pinch → fullscreen ──────────
     *
     * Drag with one finger tilts the 3D scene. Pinching outward with
     * two fingers opens the fullscreen artwork viewer — past ~1.18×
     * their initial spread we commit to fullscreen so that brief
     * two-finger scrolls don't false-trigger.
     * ───────────────────────────────────────────────────────────── */

    _bindDrag() {
      if (this._dragBound) return;
      this._dragBound = true;
      var self = this;

      // Shared pointer tracker. Two fingers → pinch mode (no tilt, just
      // watch for pinch-out past threshold). One finger → drag mode.
      var pointers = new Map();
      var mode = null;                   // 'drag' | 'pinch' | null
      var startX = 0, startY = 0;
      var startRx = 0, startRy = 0;
      var pinchStartDist = 0;
      var fsOpenedThisGesture = false;
      var moveRaf = null;

      function onDown(e) {
        if (e.button != null && e.button !== 0) return;
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (pointers.size === 1) {
          mode = 'drag';
          startX = e.clientX;
          startY = e.clientY;
          startRx = self._rx || 0;
          startRy = self._ry || 0;
          self.setAttribute('data-dragging', '1');
          try { self.setPointerCapture(e.pointerId); } catch (_) { }
        } else if (pointers.size === 2) {
          // Second finger landed: switch to pinch mode. Stop treating
          // motion as tilt for the rest of this gesture.
          mode = 'pinch';
          self.removeAttribute('data-dragging');
          var arr = Array.from(pointers.values());
          pinchStartDist = Math.hypot(arr[0].x - arr[1].x, arr[0].y - arr[1].y) || 1;
          fsOpenedThisGesture = false;
          // If we're already in fullscreen, hand this pinch off to the
          // fullscreen overlay so it can scale the stage directly.
          if (self._isFullscreen && self._fullscreen) {
            self._fullscreen.onPinchStart(pointers);
          }
        }
        e.preventDefault();
      }

      function onMove(e) {
        if (!pointers.has(e.pointerId)) return;
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (mode === 'pinch' && pointers.size === 2) {
          // In fullscreen: pinch scales the stage (zoom in further, or
          // pinch-in to dismiss and return to the gallery 3D view).
          if (self._isFullscreen && self._fullscreen) {
            self._fullscreen.onPinchMove(pointers);
            return;
          }
          // In the gallery slot: a large enough pinch-out opens fullscreen.
          if (fsOpenedThisGesture) return;
          var arr = Array.from(pointers.values());
          var dist = Math.hypot(arr[0].x - arr[1].x, arr[0].y - arr[1].y);
          // 1.18× spread ≈ "the user clearly pulled their fingers apart"
          // — big enough to filter out jitter from a pair of stationary
          // fingers, small enough to feel responsive.
          if (pinchStartDist > 0 && dist / pinchStartDist > 1.18) {
            fsOpenedThisGesture = true;
            self._openFullscreen();
            // Prime the overlay's pinch state with the current finger
            // positions so this same gesture can keep zooming in
            // smoothly instead of needing the user to re-pinch.
            if (self._isFullscreen && self._fullscreen) {
              self._fullscreen.onPinchStart(pointers);
            }
          }
          return;
        }

        if (mode !== 'drag') return;
        var dx = e.clientX - startX;
        var dy = e.clientY - startY;

        if (moveRaf) return;
        moveRaf = requestAnimationFrame(function () {
          moveRaf = null;
          // Sensitivity tuned for a natural feel
          var ry = startRy + dx * 0.35;
          var rx = startRx - dy * 0.28;
          // Clamp so the piece never spins to its back; rotX stays modest
          ry = Math.max(-55, Math.min(55, ry));
          rx = Math.max(-30, Math.min(30, rx));
          self._rx = rx;
          self._ry = ry;
          // Direct transform modification prevents style cascade recalculation
          // which allows high-polling gaming mice to drag without lagging.
          self._scene.style.transform = 'rotateX(' + rx + 'deg) rotateY(' + ry + 'deg)';

          // First real drag motion dismisses the "Drag to rotate" hint
          if (!self._hintDismissed && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
            self._hintDismissed = true;
            self.setAttribute('data-interacted', '1');
          }
        });
      }

      function onUp(e) {
        if (!pointers.has(e.pointerId)) return;
        var wasPinch = (mode === 'pinch');
        pointers.delete(e.pointerId);
        try { self.releasePointerCapture(e.pointerId); } catch (_) { }

        // If we drop below 2 fingers mid-pinch while in fullscreen,
        // tell the overlay so it can clear its pinch bookkeeping and
        // snap back to a clean state for the next gesture.
        if (wasPinch && pointers.size < 2 && self._isFullscreen && self._fullscreen) {
          self._fullscreen.onPinchEnd();
        }

        if (pointers.size === 0) {
          mode = null;
          self.removeAttribute('data-dragging');
          pinchStartDist = 0;
          fsOpenedThisGesture = false;
        } else if (pointers.size === 1 && mode === 'pinch') {
          // Dropped back to one finger mid-gesture — stay out of drag
          // mode for the rest of this interaction so the user doesn't
          // accidentally tilt the piece when lifting one finger.
        }
      }

      // Double-click / double-tap resets to the default tilt.
      function onDblClick() {
        self._rx = 4;
        self._ry = 16;
        self._scene.style.transform = 'rotateX(4deg) rotateY(16deg)';
      }

      this.addEventListener('pointerdown', onDown);
      this.addEventListener('pointermove', onMove);
      this.addEventListener('pointerup', onUp);
      this.addEventListener('pointercancel', onUp);
      this.addEventListener('lostpointercapture', onUp);
      this.addEventListener('dblclick', onDblClick);
    }

    /* Open the fullscreen viewer. The viewer hosts *this same*
       <live-mockup> element inside a full-viewport stage so tilt /
       frame / variant switching keeps working — pinch-zoom is layered
       on top of the mockup's own 3D transforms. Lazily built. */
    _openFullscreen() {
      if (this._isFullscreen) return;
      if (!this._fullscreen) this._fullscreen = new LiveMockupFullscreen(this);
      this._fullscreen.open();
    }
  }

  customElements.define('live-mockup', LiveMockup);
})();
